import React, { useEffect, useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { unwrapResult } from '@reduxjs/toolkit';
import * as Sentry from '@sentry/browser';
import chronosApi from '@chronos/api-client/chronosApi';
import * as timeUtil from '@chronos/time-util';
import eventEmitter from '@chronos/utils/eventEmitter';

import { initialUserState, useGetUsersQuery } from '@chronos/slices/usersApi';
import { useBoardContext } from '@chronos/ui-toolkit/contexts/index';
import WorklogForm from '@chronos/components/WorklogForm';
import { useGetEstimatesQuery } from '@chronos/slices/estimatesApi';
import { createWorklog, updateWorklog } from '@chronos/slices/cardSlice';
import {
  initialCardState,
  useGetTrelloBoardCardsQuery,
} from '@chronos/slices/cardsApi';
import { useDispatch } from 'react-redux';
import { trelloCardFields } from '../../constants';

const defaultErrors = {
  cardId: '',
  timeSpent: '',
  comment: '',
};

const WorklogFormContainer = React.forwardRef((props, ref) => {
  const {
    liveWorklogs,
    mode,
    cardId: initialCardId,
    lastUsedCardId,
    worklog,
    liveWorklogOrganizationId,
    adjustStartTime,
  } = props;
  const { memberId, boardId, organizationId, boardMemberPermissions } =
    useBoardContext();
  const appDispatch = useDispatch();

  const boardLiveWorklog = useMemo(
    () => liveWorklogs.find((liveWorklog) => liveWorklog.boardId === boardId),
    [liveWorklogs, boardId]
  );
  const startDateTime = worklog?.startedAt
    ? new Date(worklog.startedAt)
    : new Date();

  const worklogTime = worklog?.seconds || 0;
  const timeSpent = timeUtil.getWorklogTime(
    !worklog?.seconds ? '' : worklogTime
  );

  const [formData, setFormData] = useState({
    cardId: initialCardId,
    userId: worklog?.accountId || memberId,
    comment: worklog?.comment || '',
    startDateTime,
    adjustedStartTime: startDateTime,
    timeSpent,
    adjustStartTime,
    errors: defaultErrors,
  });
  const { cardId } = formData;

  const [totalTime, setTotalTime] = useState(0);

  const {
    data: {
      isLoading: isUsersLoading,
      entities: usersEntities,
    } = initialUserState,
  } = useGetUsersQuery({
    boardId,
    organizationId,
  });
  const {
    isLoading: isCardsLoading,
    data: { entities: cardsEntities } = initialCardState,
  } = useGetTrelloBoardCardsQuery({
    boardId,
    filter: 'visible',
    fields: trelloCardFields,
  });
  const { data: estimates = [] } = useGetEstimatesQuery(
    {
      cardIds: [cardId],
    },
    {
      skip: !cardId,
    }
  );

  useEffect(() => {
    if (!cardId) return;
    (async () => {
      try {
        const worklogsMeta = await chronosApi.getWorklogsMeta({
          body: { cardIds: [cardId] },
        });
        setTotalTime(worklogsMeta.totalTime);
      } catch (err) {
        Sentry.captureException(err);
      }
    })();
  }, [cardId]);

  const handleChangeFormData = useCallback(
    (values) => {
      setFormData((prevFormData) => ({ ...prevFormData, ...values }));
    },
    [setFormData]
  );

  useEffect(() => {
    (async () => {
      if (initialCardId || !lastUsedCardId) return;

      handleChangeFormData({
        cardId: lastUsedCardId,
      });
    })();
  }, [initialCardId, lastUsedCardId]);

  const validate = useCallback(() => {
    let valid = true;

    const condition =
      mode === 'startReview'
        ? false
        : formData.timeSpent === '' || !timeUtil.jts(formData.timeSpent);

    const isCommentRequired =
      boardMemberPermissions.isCommentRequired &&
      mode !== 'startReview' &&
      !formData.comment.length;

    if (!formData.cardId || condition || isCommentRequired) {
      valid = false;
    }
    handleChangeFormData({
      errors: {
        cardId: formData.cardId ? '' : 'Card is required',
        comment: isCommentRequired
          ? 'Work log comment is required for you'
          : '',
        timeSpent: condition ? 'Invalid time format' : '',
      },
    });

    return valid;
  }, [
    formData,
    boardMemberPermissions.isCommentRequired,
    handleChangeFormData,
  ]);

  const selectedCard = useMemo(() => cardsEntities[cardId] || null, [cardId]);
  const saveWorklog = async (values) => {
    if (values.id) {
      return appDispatch(updateWorklog(values));
    }
    const worklogBoardId = selectedCard?.idBoard || boardId;
    return appDispatch(
      createWorklog({
        boardId: worklogBoardId,
        ...values,
      })
    );
  };

  const startTimer = (values) => {
    const nowTimestamp = +new Date();
    const timeSpentInSeconds = timeUtil.jts(values.timeSpent);

    const cardTimerState = {
      memberId,
      boardId,
      timerStatus: 'run',
      startTime: nowTimestamp - timeSpentInSeconds * 1000,
      trackingWorklogComment: values.comment || null,
      cardId: values.cardId,
      pauseStartTime: null,
      pauseTrackedSeconds: 0,
    };

    return chronosApi.setTimerState({
      body: {
        fastResponse: true,
        cardId: values.cardId,
        boardId: cardTimerState.boardId,
        ...(liveWorklogOrganizationId
          ? {
              organizationId: liveWorklogOrganizationId,
            }
          : {}),
        state: cardTimerState,
      },
    });
  };

  const stopTimer = (values) => {
    try {
      const worklogBoardId = selectedCard?.idBoard || boardId;

      return saveWorklog({
        type: 'auto',
        // In case if user change a card it needs to delete timerState on the backend
        timerCardId: boardLiveWorklog?.cardId,
        cardId: values.cardId,
        boardId: worklogBoardId,
        ...(liveWorklogOrganizationId
          ? {
              organizationId: liveWorklogOrganizationId,
            }
          : {}),
        ...values,
      });
    } catch (err) {
      console.log(err);
      Sentry.captureException(err);
      return Promise.resolve(null);
    }
  };

  const handleSubmit = useCallback(async () => {
    const isValid = validate();
    if (isValid) {
      try {
        const timeSpentInSeconds = timeUtil.jts(formData.timeSpent);
        const formattedStartTime = (
          formData.adjustStartTime
            ? formData.adjustedStartTime
            : formData.startDateTime
        ).toISOString();

        const values = {
          cardId,
          startedAt: formattedStartTime,
          seconds: timeSpentInSeconds,
          comment: formData.comment,
          account: {
            id: formData.userId,
          },
        };
        props.onSaving({ values });
        eventEmitter.emit('savingWorklog', { mode, values });

        let promise = Promise.resolve();
        if (mode === 'startReview') {
          promise = startTimer({
            ...values,
            timeSpent: formData.timeSpent,
          });
        }

        if (mode === 'stopReview') {
          promise = stopTimer(values);
        }

        if (['addWorklog', 'editWorklog'].includes(mode)) {
          promise = saveWorklog({
            id: worklog?.id,
            ...values,
          });
        }

        const response = unwrapResult(await promise);
        if (props.onSaved) {
          props.onSaved({ mode, values, response });
        }
        eventEmitter.emit('savedWorklog', { mode, values, response });
      } catch (err) {
        console.error(err);
        Sentry.captureException(err);
      }

      handleChangeFormData({
        errors: defaultErrors,
      });
    }
  }, [mode, formData, worklog, props.onSaved, cardId, validate]);

  const usersSelectProps = useMemo(
    () => ({
      isDisabled: !!worklog?.id,
      isLoading: isUsersLoading,
      items: Object.values(usersEntities),
    }),
    [usersEntities, worklog?.id, isUsersLoading]
  );
  const cardsSelectProps = useMemo(
    () => ({
      isDisabled: mode === 'editWorklog',
      isLoading: isCardsLoading,
      items: Object.values(cardsEntities),
    }),
    [cardsEntities, mode, isCardsLoading]
  );

  // parent component can call submit() on ref
  React.useImperativeHandle(ref, () => ({
    submit: () => {
      handleSubmit();
    },
  }));

  return (
    <WorklogForm
      estimates={estimates}
      usersSelectProps={usersSelectProps}
      cardsSelectProps={cardsSelectProps}
      totalTime={totalTime}
      mode={mode}
      skin="pwa"
      setFormData={handleChangeFormData}
      formData={formData}
      worklogTime={worklogTime}
      isCommentRequired={boardMemberPermissions.isCommentRequired}
      handleSubmit={handleSubmit}
    />
  );
});

WorklogFormContainer.defaultProps = {
  worklog: null,
  mode: '',
  adjustStartTime: true,
  liveWorklogs: [],
};

WorklogFormContainer.propTypes = {
  mode: PropTypes.oneOf([
    'addWorklog',
    'editWorklog',
    'startReview',
    'stopReview',
  ]),
  worklog: PropTypes.shape({
    id: PropTypes.number,
    startedAt: PropTypes.string,
    seconds: PropTypes.number,
    comment: PropTypes.string,
    accountId: PropTypes.string,
  }),
  cardId: PropTypes.string.isRequired,
  onSaving: PropTypes.func,
  onSaved: PropTypes.func,
  adjustStartTime: PropTypes.bool,
  liveWorklogs: PropTypes.array,
  lastUsedCardId: PropTypes.string,
  liveWorklogOrganizationId: PropTypes.string,
};

WorklogFormContainer.displayName = 'WorklogFormContainer';

export default WorklogFormContainer;
